// Recent POs of one customer inside the customer detail (Ord_CustomerDetail). The supplier can open a PO or
// answer a «Ирсэн» one without leaving the screen. Decisions go through the same status x action matrix
// and the same 440 confirm dialog as the PO list (the amount is repeated before a decline).

import { Box, Group, Modal, Skeleton, Stack, Table, Text, Title } from "@mantine/core";
import { CheckIcon, EyeIcon, XIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { HelpIcon } from "@dehub/theme";
import { useSalesActions } from "../order/order-actions";
import { PurchaseSalesConfirmForm, PURCHASE_CONFIRM_MODAL_SIZE } from "../purchase-sales/confirm-form";
import { RowActionIcons } from "../purchase-sales/list-table";
import { OrderStatusTag } from "../purchase-sales/status-tag";
import { ListState } from "../ui";
import RenderText from "../ui/render-text";
import { IOrderPlus } from "../../models/Order";
import { dateFormat } from "../../utils/date";
import { useCustomerOrders } from "./hooks";
import classes from "./customer-orders-section.module.css";

interface Props {
  customerBusinessId: string;
}

export function CustomerOrdersSection({ customerBusinessId }: Props) {
  const { t } = useTranslation(["supplierSales", "common"]);
  const navigate = useNavigate();
  const { stateOf } = useSalesActions();
  const { orders, total, isLoading, error, refresh } = useCustomerOrders({ senderBusinessId: customerBusinessId, limit: 10 });
  const [action, setAction] = useState<string[]>([]);

  const rowActions = (o: IOrderPlus) => {
    const detail = stateOf("detail", o);
    const approve = stateOf("approve", o);
    const decline = stateOf("decline", o);
    return [
      { key: "detail", label: t("common:actions.detail"), icon: <EyeIcon size={18} />, reason: detail.reason, onClick: () => o.id && navigate(`/supplier-purchase/detail/${o.id}`) },
      ...(approve.applies ? [{ key: "approve", label: t("customer.respondApprove"), icon: <CheckIcon size={18} />, reason: approve.reason, onClick: () => setAction(["confirm", o as never, "confirm"]) }] : []),
      ...(decline.applies ? [{ key: "decline", label: t("customer.respondReject"), icon: <XIcon size={18} />, reason: decline.reason, onClick: () => setAction(["reject", o as never, "reject"]) }] : []),
    ];
  };

  return (
    <Box className={classes.card}>
      <Group justify="space-between" align="flex-start" className={classes.head}>
        <Stack gap={2}>
          <Title order={3} className={classes.title}>
            {t("customer.recentOrdersTitle")}
            <HelpIcon helpKey="order.supplier-customer-detail.section.recent-orders" variant="section" />
          </Title>
          <Text fz={14} c="gray.8">{t("customer.recentOrdersSubtitle")}</Text>
        </Stack>
        <Text fz={14} c="gray.8">{t("common:table.total")}: <Text span fw={700} c="gray.9">{total}</Text></Text>
      </Group>

      {error ? (
        <Box p="lg"><ListState state="error" onRetry={() => refresh()} /></Box>
      ) : isLoading ? (
        <Stack gap="xs" p="lg">
          <Skeleton height={20} />
          <Skeleton height={20} />
          <Skeleton height={20} />
        </Stack>
      ) : orders.length === 0 ? (
        <Box p="lg"><ListState state="empty" title={t("customer.recentOrdersEmpty")} /></Box>
      ) : (
        <Table highlightOnHover>
          <Table.Thead bg="var(--nw-table-head-bg)">
            <Table.Tr>
              <Table.Th>{t("common:table.action")}</Table.Th>
              <Table.Th>{t("customer.orderCode")}</Table.Th>
              <Table.Th>{t("customer.orderDate")}</Table.Th>
              <Table.Th>{t("customer.orderStatus")}</Table.Th>
              <Table.Th ta="right">{t("customer.orderAmount")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {orders.map((o) => (
              <Table.Tr key={o.id}>
                <Table.Td><RowActionIcons items={rowActions(o)} /></Table.Td>
                <Table.Td><Text fz={14} ff="monospace">{o.purchaseCode ?? "-"}</Text></Table.Td>
                <Table.Td><Text fz={14} ff="monospace">{o.createdAt ? dateFormat(o.createdAt) : "-"}</Text></Table.Td>
                <Table.Td><OrderStatusTag code={o.orderStatus} /></Table.Td>
                <Table.Td ta="right">
                  {/* totalAmount is what the buyer pays; orderAmount is the amount before discounts (O78). */}
                  <RenderText text={Number(o.totalAmount ?? 0)} isAmount plainAmount fontWeight={700} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={action[0] === "confirm" || action[0] === "reject"} onClose={() => setAction([])} withCloseButton={false} size={PURCHASE_CONFIRM_MODAL_SIZE} padding={0} centered>
        <PurchaseSalesConfirmForm action={action[1] as never} setActions={setAction} constant={action[2]} reload={() => refresh()} />
      </Modal>
    </Box>
  );
}
